import React, { useState, useEffect } from 'react'
import { assetService } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { formatCurrency } from '../utils/currency'
import { formatDate, formatDateForInput } from '../utils/dateFormat'
import { getApiUrl } from '../utils/apiConfig'
import TablePagination from '../components/TablePagination'
import ModalFeedback from '../components/ModalFeedback'
import useRolePermissions from '../hooks/useRolePermissions'

export default function Assets() {
  const { user } = useAuth()
  const { isDark } = useTheme()
  const { canMutate, isViewer } = useRolePermissions()
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [showModal, setShowModal] = useState(false)
  const [modalData, setModalData] = useState(null)
  const [modalType, setModalType] = useState('create')

  useEffect(() => {
    fetchAssets()
  }, [])

  const fetchAssets = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await assetService.getAll()
      setAssets(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch assets:', error)
      setError(error.response?.data?.message || error.message || 'Failed to fetch assets')
    } finally {
      setLoading(false)
    }
  }

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = searchTerm === '' ||
      asset.asset_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.asset_code.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === '' || asset.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const paginatedAssets = filteredAssets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const handleCreate = () => {
    setModalType('create')
    setModalData({
      asset_code: '',
      asset_name: '',
      category_id: '',
      purchase_date: '',
      purchase_price: '',
      current_value: '',
      location: '',
      condition_status: 'Good',
      serial_number: '',
      description: '',
      remarks: '',
      // Financial integration options
      payment_method: 'cash', // cash or financed
      create_journal_entry: true,
      loan_account_id: ''
    })
    setShowModal(true)
  }

  const handleEdit = (asset) => {
    setModalType('edit')
    setModalData({ ...asset })
    setShowModal(true)
  }

  const handleDelete = async (assetId) => {
    if (!confirm('Are you sure you want to delete this asset?')) return

    try {
      await assetService.delete(assetId)
      await fetchAssets()
      showSuccess('Asset deleted successfully')
    } catch (error) {
      console.error('Failed to delete asset:', error)
      showError(error.response?.data?.message || 'Failed to delete asset')
    }
  }

  const handleSave = async (formData) => {
    try {
      if (modalType === 'create') {
        // First create the asset
        const assetResponse = await assetService.create(formData)

        // If journal entry creation is requested, create it
        if (formData.create_journal_entry && formData.payment_method && formData.purchase_price) {
          await createAssetJournalEntry({
            assetId: assetResponse.data.asset_id,
            assetName: formData.asset_name,
            purchasePrice: parseFloat(formData.purchase_price),
            paymentMethod: formData.payment_method,
            loanAccountId: formData.loan_account_id,
            purchaseDate: formData.purchase_date
          })
          showSuccess('Asset created with journal entry recorded successfully')
        } else {
          showSuccess('Asset created successfully')
        }
      } else {
        await assetService.update(formData.asset_id, formData)
        showSuccess('Asset updated successfully')
      }
      setShowModal(false)
      await fetchAssets()
    } catch (error) {
      console.error('Failed to save asset:', error)
      showError(error.response?.data?.message || 'Failed to save asset')
    }
  }

  const createAssetJournalEntry = async ({ assetId, assetName, purchasePrice, paymentMethod, loanAccountId, purchaseDate }) => {
    try {
      const token = localStorage.getItem('token')
      const entryData = {
        entry_date: purchaseDate,
        reference: `ASSET-${assetId}`,
        description: `Asset Purchase: ${assetName}`,
        lines: []
      }

      if (paymentMethod === 'cash') {
        // Dr Fixed Assets / Cr Cash
        entryData.lines = [
          {
            account_id: 5, // Fixed Assets account
            debit_amount: purchasePrice,
            credit_amount: 0,
            description: `Asset Purchase: ${assetName}`
          },
          {
            account_id: 1, // Cash and Bank
            debit_amount: 0,
            credit_amount: purchasePrice,
            description: `Payment for: ${assetName}`
          }
        ]
      } else if (paymentMethod === 'financed' && loanAccountId) {
        // Dr Fixed Assets / Cr Loan Payable
        entryData.lines = [
          {
            account_id: 5, // Fixed Assets account
            debit_amount: purchasePrice,
            credit_amount: 0,
            description: `Asset Purchase: ${assetName}`
          },
          {
            account_id: parseInt(loanAccountId), // Loan Payable account
            debit_amount: 0,
            credit_amount: purchasePrice,
            description: `Financed asset: ${assetName}`
          }
        ]
      }

      await fetch(getApiUrl('/journal-entries'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(entryData)
      })
    } catch (error) {
      console.error('Failed to create journal entry:', error)
      throw error
    }
  }

  const handleDepreciation = async (assetId) => {
    const amount = prompt('Enter depreciation amount:')
    if (!amount || isNaN(amount)) return

    try {
      await assetService.recordDepreciation(assetId, {
        amount: parseFloat(amount),
        depreciation_date: new Date().toISOString().split('T')[0],
        description: 'Manual depreciation entry'
      })
      showSuccess('Depreciation recorded successfully')
      await fetchAssets()
    } catch (error) {
      console.error('Failed to record depreciation:', error)
      showError(error.response?.data?.message || 'Failed to record depreciation')
    }
  }

  const showSuccess = (message) => {
    setModalData({ type: 'success', message })
    setModalType('feedback')
    setShowModal(true)
  }

  const showError = (message) => {
    setModalData({ type: 'error', message })
    setModalType('feedback')
    setShowModal(true)
  }

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-800',
      'Under Maintenance': 'bg-yellow-100 text-yellow-800',
      'Fully Depreciated': 'bg-red-100 text-red-800',
      'Disposed': 'bg-gray-100 text-gray-800',
      'Sold': 'bg-blue-100 text-blue-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getConditionColor = (condition) => {
    const colors = {
      'New': 'bg-green-100 text-green-800',
      'Good': 'bg-blue-100 text-blue-800',
      'Fair': 'bg-yellow-100 text-yellow-800',
      'Poor': 'bg-red-100 text-red-800'
    }
    return colors[condition] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className={`rounded-lg p-8 text-center ${isDark ? 'bg-slate-900 text-white' : 'bg-white'}`}>
        <div className="text-4xl mb-4">⏳</div>
        <p className={isDark ? 'text-slate-300' : 'text-gray-600'}>Loading assets...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`rounded-lg p-8 text-center ${isDark ? 'bg-slate-900 text-white' : 'bg-white'}`}>
        <div className="text-4xl mb-4">⚠️</div>
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchAssets}
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Assets Management</h1>
        {canMutate && (
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
          >
            + Add Asset
          </button>
        )}
      </div>

      {/* Filters */}
      <div className={`rounded-lg p-4 shadow ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or code..."
              className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'border-slate-600 bg-slate-800 text-white' : 'border-gray-300 text-gray-700'
              }`}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'border-slate-600 bg-slate-800 text-white' : 'border-gray-300 text-gray-700'
              }`}
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Under Maintenance">Under Maintenance</option>
              <option value="Fully Depreciated">Fully Depreciated</option>
              <option value="Disposed">Disposed</option>
              <option value="Sold">Sold</option>
            </select>
          </div>
        </div>
      </div>

      {/* Assets Table */}
      <div className={`rounded-lg shadow overflow-hidden ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="overflow-x-auto">
          <table className={`w-full ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            <thead className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Asset Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Asset Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Purchase Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Current Value</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">NBV</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Condition</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
              {paginatedAssets.length > 0 ? (
                paginatedAssets.map((asset) => (
                  <tr key={asset.asset_id} className={isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 text-sm font-medium">{asset.asset_code}</td>
                    <td className="px-4 py-3 text-sm">{asset.asset_name}</td>
                    <td className="px-4 py-3 text-sm">{formatCurrency(asset.purchase_price)}</td>
                    <td className="px-4 py-3 text-sm">{formatCurrency(asset.current_value)}</td>
                    <td className="px-4 py-3 text-sm font-medium">{formatCurrency(asset.net_book_value)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                        {asset.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConditionColor(asset.condition_status)}`}>
                        {asset.condition_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="flex justify-end gap-2">
                        {canMutate && (
                          <>
                            <button
                              onClick={() => handleEdit(asset)}
                              className="text-blue-600 hover:text-blue-800 font-medium text-xs"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDepreciation(asset.asset_id)}
                              className="text-orange-600 hover:text-orange-800 font-medium text-xs"
                              disabled={asset.status === 'Fully Depreciated'}
                            >
                              Depr.
                            </button>
                            <button
                              onClick={() => handleDelete(asset.asset_id)}
                              className="text-red-600 hover:text-red-800 font-medium text-xs"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-sm">
                    No assets found. {canMutate && 'Click "Add Asset" to create one.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          totalItems={filteredAssets.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>

      {/* Asset Modal */}
      {showModal && modalType !== 'feedback' && (
        <AssetModal
          show={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          initialData={modalData}
          type={modalType}
          isDark={isDark}
        />
      )}

      {/* Feedback Modal */}
      {showModal && modalType === 'feedback' && (
        <ModalFeedback
          show={showModal}
          onClose={() => setShowModal(false)}
          type={modalData?.type}
          message={modalData?.message}
        />
      )}
    </div>
  )
}

// Asset Modal Component
function AssetModal({ show, onClose, onSave, initialData, type, isDark }) {
  const [formData, setFormData] = useState(initialData || {})
  const [errors, setErrors] = useState({})

  useEffect(() => {
    setFormData(initialData || {})
  }, [initialData])

  const validate = () => {
    const newErrors = {}
    if (!formData.asset_name) newErrors.asset_name = 'Asset name is required'
    if (!formData.purchase_price) newErrors.purchase_price = 'Purchase price is required'
    if (!formData.purchase_date) newErrors.purchase_date = 'Purchase date is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validate()) {
      onSave(formData)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
        <div className={`p-6 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <h2 className="text-xl font-bold">
            {type === 'create' ? 'Create New Asset' : 'Edit Asset'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Asset Code</label>
              <input
                type="text"
                value={formData.asset_code || ''}
                onChange={(e) => setFormData({ ...formData, asset_code: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
                placeholder="Auto-generated if empty"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Asset Name *</label>
              <input
                type="text"
                value={formData.asset_name || ''}
                onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
              {errors.asset_name && <p className="text-red-500 text-xs mt-1">{errors.asset_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Purchase Price *</label>
              <input
                type="number"
                step="0.01"
                value={formData.purchase_price || ''}
                onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
              {errors.purchase_price && <p className="text-red-500 text-xs mt-1">{errors.purchase_price}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Purchase Date *</label>
              <input
                type="date"
                value={formData.purchase_date || ''}
                onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
              {errors.purchase_date && <p className="text-red-500 text-xs mt-1">{errors.purchase_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <input
                type="text"
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Condition</label>
              <select
                value={formData.condition_status || 'Good'}
                onChange={(e) => setFormData({ ...formData, condition_status: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              >
                <option value="New">New</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Serial Number</label>
              <input
                type="text"
                value={formData.serial_number || ''}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={formData.status || 'Active'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              >
                <option value="Active">Active</option>
                <option value="Under Maintenance">Under Maintenance</option>
                <option value="Fully Depreciated">Fully Depreciated</option>
                <option value="Disposed">Disposed</option>
                <option value="Sold">Sold</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Remarks</label>
            <textarea
              value={formData.remarks || ''}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              rows="2"
              className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
              }`}
            />
          </div>

          {/* Financial Integration Section */}
          {type === 'create' && (
            <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-blue-50'}`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                <span>📝</span>
                <span>Financial Integration</span>
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.create_journal_entry || false}
                      onChange={(e) => setFormData({ ...formData, create_journal_entry: e.target.checked })}
                      className="rounded"
                    />
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
                      Create journal entry for this purchase?
                    </span>
                  </label>
                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                    Automatically record this asset purchase in your financial statements
                  </p>
                </div>

                {formData.create_journal_entry && (
                  <>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                        Payment Method *
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="payment_method"
                            value="cash"
                            checked={formData.payment_method === 'cash'}
                            onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                            className="rounded"
                          />
                          <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
                            💵 Cash Purchase
                          </span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="payment_method"
                            value="financed"
                            checked={formData.payment_method === 'financed'}
                            onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                            className="rounded"
                          />
                          <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
                            🏦 Financed (Loan)
                          </span>
                        </label>
                      </div>
                    </div>

                    {formData.payment_method === 'financed' && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                          Loan Account *
                        </label>
                        <select
                          value={formData.loan_account_id || ''}
                          onChange={(e) => setFormData({ ...formData, loan_account_id: e.target.value })}
                          className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark ? 'border-slate-600 bg-slate-700 text-white' : 'border-gray-300 text-gray-700'
                          }`}
                          required
                        >
                          <option value="">Select Loan Account</option>
                          <option value="9">Short-term Loans</option>
                          <option value="12">Long-term Loans</option>
                        </select>
                      </div>
                    )}

                    {/* Journal Entry Preview */}
                    <div className={`p-3 rounded ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                      <p className={`text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        📝 Automatic Journal Entry Preview:
                      </p>
                      {formData.payment_method === 'cash' && (
                        <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                          <span className="font-medium text-green-600">Dr</span> Fixed Assets {formatCurrency(parseFloat(formData.purchase_price) || 0)}<br/>
                          <span className="font-medium text-red-600">Cr</span> Cash and Bank {formatCurrency(parseFloat(formData.purchase_price) || 0)}
                        </div>
                      )}
                      {formData.payment_method === 'financed' && (
                        <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                          <span className="font-medium text-green-600">Dr</span> Fixed Assets {formatCurrency(parseFloat(formData.purchase_price) || 0)}<br/>
                          <span className="font-medium text-red-600">Cr</span> Loan Payable {formatCurrency(parseFloat(formData.purchase_price) || 0)}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </form>
        <div className={`p-6 border-t flex justify-end gap-3 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 rounded-lg font-medium transition ${isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
          >
            {type === 'create' ? 'Create Asset' : 'Update Asset'}
          </button>
        </div>
      </div>
    </div>
  )
}